import {
  Control,
  Controller,
  FieldErrors,
  UseFormRegister,
} from 'react-hook-form';
import CustomToggle from '../../../../../../../shared/CustomToggle';
import AssetFormField from './AssetFormField';
import { AssetFormData, AssetFormMode, AssetType, AssetView } from './types';
import { capitalize } from '../../../../../../../utils/capitalize';

interface AssetConditionProps {
  mode: AssetFormMode;
  register: UseFormRegister<AssetFormData>;
  control: Control<AssetFormData>;
  errors: FieldErrors<AssetFormData>;
  assetType: AssetType;
  /** Undefined until edit prefill resolves; out-of-commission reason is gated on it. */
  status?: AssetFormData['status'];
  /** ownershipStatus !== 'owned' — controls the PM/repair responsibility toggle. */
  showPMSField: boolean;
  /** Edit only: source of the read-only engine-condition display. */
  selectedAsset?: AssetView | null;
}

/**
 * Operational "right column": tire brand quality, (truck only) commission
 * status and out-of-commission reason, condition, and the maintenance-
 * responsibility toggle. Per the legacy forms: in CREATE, engine condition is
 * an editable toggle and the PM/repair toggle shows only when not owned; in
 * EDIT, engine condition is read-only (truck) and the PM/repair toggle always
 * shows.
 */
export default function AssetCondition({
  mode,
  register,
  control,
  errors,
  assetType,
  status,
  showPMSField,
  selectedAsset,
}: AssetConditionProps) {
  const isEdit = mode === 'edit';
  const engineCondition = selectedAsset?.engine_condition ?? '';
  return (
    <div className="flex w-full flex-col items-start gap-y-4 md:w-1/2">
      <div className="w-full space-y-1">
        <p className="text-[14px] text-[#7D8191]">Tire Brand Quality</p>
        <Controller
          name="tireBrandQuality"
          control={control}
          render={({ field: { value, onChange } }) => (
            <CustomToggle
              value={value}
              onChange={onChange}
              options={[
                { label: 'Budget', value: 'budget' },
                { label: 'Premium', value: 'premium' },
              ]}
            />
          )}
        />
      </div>

      {assetType === 'truck' && (
        <div className="w-full space-y-1">
          <p className="text-[14px] text-[#7D8191]">Status</p>
          <Controller
            name="status"
            control={control}
            render={({ field: { value, onChange } }) => (
              <CustomToggle
                value={value}
                onChange={onChange}
                options={[
                  { label: 'In Commission', value: 'in_commission' },
                  { label: 'Out of Commission', value: 'out_of_commission' },
                ]}
              />
            )}
          />
        </div>
      )}

      {assetType === 'truck' && status === 'out_of_commission' && (
        <AssetFormField
          id="outOfServiceReason"
          label="Reason for Being Out of Commission"
          error={errors.outOfServiceReason?.message}
        >
          <select
            id="outOfServiceReason"
            {...register('outOfServiceReason', {
              required: 'Reason for being out of commission is required',
            })}
            className={`mt-1 block w-full border px-3 py-2 ${
              errors.outOfServiceReason ? 'border-red-500' : 'border-gray-300'
            } rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
          >
            <option value="preventative_maintenance">
              Preventative Maintenance
            </option>
            <option value="repairs">Repairs</option>
            <option value="slow_business">Slow Business</option>
            <option value="driver_unavailable">Driver Unavailable</option>
            <option value="regulatory_hold">Regulatory Hold</option>
          </select>
        </AssetFormField>
      )}

      {isEdit
        ? assetType === 'truck' && (
            <div className="w-full space-y-1">
              <p className="text-[14px] text-[#7D8191]">Engine Condition</p>
              <div className="flex flex-row items-center space-x-1 align-middle">
                <p className="text-[16px] text-primary">
                  {capitalize(engineCondition)}
                </p>
              </div>
            </div>
          )
        : (
            <div className="w-full space-y-1">
              <p className="text-[14px] text-[#7D8191]">Condition</p>
              <Controller
                name="condition"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <CustomToggle
                    value={value}
                    onChange={onChange}
                    options={[
                      { label: 'Unmodified', value: 'unmodified' },
                      { label: 'Rebuilt', value: 'rebuilt' },
                    ]}
                  />
                )}
              />
            </div>
          )}

      {(isEdit || showPMSField) && (
        <div className="w-full space-y-1">
          <p className="text-[14px] text-[#7D8191]">
            Responsibility for Maintenance and Repairs
          </p>
          <Controller
            name="paysForPMSRepairs"
            control={control}
            render={({ field: { value, onChange } }) => (
              <CustomToggle
                value={value}
                onChange={onChange}
                options={[
                  { label: 'Pay', value: true },
                  { label: 'Not Pay', value: false },
                ]}
              />
            )}
          />
        </div>
      )}
    </div>
  );
}
